import { useEffect, useState, useRef, useMemo, useContext, useCallback } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Avatar from "../../../assets/img/avatar.png";
import { useNavigate } from "react-router-dom";
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  getGridStringOperators,
  getGridNumericOperators,
  useGridApiRef,
  GridColumnsPanel,
} from "@mui/x-data-grid-pro";
import LinearProgress from "@mui/material/LinearProgress";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { toast } from "react-toastify";
import debounce from "lodash.debounce";
import axios from "axios";

import useUser from "../../../hooks/user";
import CustomDensitySelector from "./CustomDensitySelector";
import CustomSortSelector from "./CustomSortSelector";
import { ApiManager } from "../../../servicemanager/apimanager";
import { LoaderContext } from "../../../context/LoaderContext";
import { __API_URL__ } from "../../../SERVER_URL";
import logUserAction from "../../../apis/userLogger";
import CustomButton from "../../UI/CustomButton";
import useDeletePatients from "../../../hooks/useDeletePatientsMutation";
import { USER_ROLES } from "../../../utils/constants";

dayjs.extend(utc);

const defaultSearchObject = {
  page: 1,
  page_size: 10,
  trial_name: "",
  trial_name_operator: "",
  first_name: "",
  first_name_operator: "",
  last_name: "",
  last_name_operator: "",
  email: "",
  email_operator: "",
  participant_id: "",
  site_id: "",
  completed_photo_session_count: "",
  clinic: "",
  trial_status: "",
  ordering: "",
  has_trial: "",
  upcoming_visit_start_date: "",
  drug_id: "",
  patient_status: "",
  patient_status_operator: "",
  preferred_language: "",
  preferred_language_operator: "",
  fields: "",
};

const EMPTY_SELECTION = {
  ids: new Set(),
  type: "include",
};

function CustomToolbar({
  selectedValue,
  handleChangeTrial,
  trialNames,
  setPatientTableConfigurations,
  patientTableConfigurations,
  setFilterConfigurationLastStateSaved,
  handleSaveFilters,
  filterConfigurationLastStateSaved,
  handleCSVButtonClick,
  role,
  setOpenConfirmDeletePatientsDialog,
  selectedCount,
  handleSortModelChange,
}) {
  return (
    <GridToolbarContainer
      sx={{
        justifyContent: "flex-start",
        gap: 1.5,
        flexWrap: "wrap",
      }}
    >
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Trial Name</InputLabel>
        <Select
          value={selectedValue}
          onChange={handleChangeTrial}
          label="Trial Name"
          sx={{ width: 200 }}
        >
          {trialNames.map((trial) => (
            <MenuItem key={trial.id} value={trial.name}>
              {trial.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <CustomDensitySelector
        setPatientTableConfigurations={setPatientTableConfigurations}
        patientTableConfigurations={patientTableConfigurations}
        setFilterConfigurationLastStateSaved={setFilterConfigurationLastStateSaved}
      />
      <CustomSortSelector
        patientTableConfigurations={patientTableConfigurations}
        onSortModelChange={handleSortModelChange}
      />

      <CustomButton
        onClick={() => handleSaveFilters()}
        disabled={filterConfigurationLastStateSaved && true}
        size="small"
      >
        Save Filters
      </CustomButton>

      <CustomButton onClick={handleCSVButtonClick} size="small">
        Export to Excel
      </CustomButton>

      {role === USER_ROLES.SUPER_ADMIN && (
        <CustomButton
          onClick={() => setOpenConfirmDeletePatientsDialog(true)}
          size="small"
          disabled={selectedCount <= 0}
          data-testid="delete-patients-btn"
        >
          Delete Selected Subjects
        </CustomButton>
      )}
    </GridToolbarContainer>
  );
}

function CustomColumnsPanel(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        maxHeight: "100%",
        width: "100%",
        boxSizing: "border-box",
        padding: 2,
      }}
    >
      <Box display="flex" justifyContent="center" mb={1} px={1}>
        <Typography
          variant="body1"
          sx={{
            color: "black",
            textAlign: "center",
            wordBreak: "break-word",
            maxWidth: "100%",
          }}
        >
          Click "Save Filter" to apply changes
        </Typography>
      </Box>

      <GridColumnsPanel {...props} />
    </Box>
  );
}

export default function PatientTabel(props) {
  const TOKEN = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data?.role;
  const token = localStorage.getItem("token");
  const user = useUser();
  const loaderContext = useContext(LoaderContext);
  let navigate = useNavigate();

  const deletePatientsMutation = useDeletePatients();

  const clinicPatient = props.clinicPatient;
  const typeOfPatient = props.typeOfPatient;
  const setRowSelectionModel = props.setRowSelectionModel;
  const rowSelectionModel = props.rowSelectionModel;
  const changedPatientTable = props.changedPatientTable;

  const apiRef = useGridApiRef();

  const initialRenderForSearch = useRef(true);
  const isFromDropdown = useRef(false);

  const [openConfirmDeletePatientsDialog, setOpenConfirmDeletePatientsDialog] = useState(false);
  const [refetchPatients, setRefetchPatients] = useState(false);
  const [data, setData] = useState({ results: [], count: 0 });
  const [columns, setColumns] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [patientTableConfigurations, setPatientTableConfigurations] = useState({
    filters: [],
    sort: [],
    noOfRows: 10,
    density: "",
  });
  const [localFilterModel, setLocalFilterModel] = useState({
    items: patientTableConfigurations.filters,
  });
  const [filterConfigurationLastStateSaved, setFilterConfigurationLastStateSaved] = useState(true);
  const [selectedValue, setSelectedValue] = useState("");
  const [trialNames, setTrialNames] = useState([{ id: -1, name: "None" }]);
  const [search, setSearch] = useState({});
  const [apiCallStatus, setApiCallStatus] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const numberFiltersAllowed = [">", "<", "!=", "=", "<=", ">="];
  const myColumnsObject = {
    id: {
      field: "id",
      headerName: "Id",
      type: "text",
      sortable: true,
      width: 120,
      align: "left",
      headerAlign: "left",
      editable: false,
      filterable: false,
      valueGetter: (_v, row) => row?.id ?? "",
    },

    drug_id: {
      field: "drug_id",
      headerName: "Drug Kit Id",
      type: "number",
      sortable: true,
      width: 120,
      align: "left",
      headerAlign: "left",
      editable: false,
      filterable: true,
      valueGetter: (_v, row) => row?.all_data?.drug_id ?? null,
    },

    patient_status: {
      field: "patient_status",
      headerName: "Subject Status",
      sortable: false,
      width: 120,
      align: "left",
      headerAlign: "left",
      editable: false,
      filterable: true,
      filterOperators: getGridStringOperators().filter((op) =>
        ["contains", "equals"].includes(op.value)
      ),
      valueGetter: (_v, row) => row?.all_data?.patient_status ?? "",
    },

    profile_photo: {
      field: "profile_photo",
      headerName: "Profile Photo",
      type: "image",
      sortable: false,
      width: 80,
      align: "left",
      headerAlign: "left",
      editable: false,
      filterable: false,
      valueGetter: (_v, row) => {
        const imageUrl = row?.thumbnail?.thumbnail ? `${row.thumbnail.thumbnail}` : Avatar;
        return `<img src="${imageUrl}" alt="img" style="width:40px;height:46px;border-radius:50%;object-fit:cover;" />`;
      },
      renderCell: (params) => <div dangerouslySetInnerHTML={{ __html: params.value }} />,
    },

    trial_name: {
      field: "trial_name",
      headerName: "Trial Name",
      sortable: false,
      width: 150,
      align: "left",
      headerAlign: "left",
      filterable: true,
      filterOperators: getGridStringOperators().filter((op) =>
        ["contains", "equals"].includes(op.value)
      ),
      valueGetter: (_v, row) => row?.all_data?.trial_name ?? "",
    },

    trial_status: {
      field: "trial_status",
      headerName: "Trial Status",
      sortable: false,
      width: 140,
      align: "left",
      headerAlign: "left",
      filterable: true,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
      valueGetter: (_v, row) => row?.all_data?.trial_status ?? "",
    },

    site_id: {
      field: "site_id",
      headerName: "Site Id",
      width: 90,
      align: "left",
      headerAlign: "left",
      sortable: true,
      filterable: true,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
      valueGetter: (_v, row) => row?.all_data?.site_id ?? "",
    },

    participant_id: {
      field: "participant_id",
      headerName: "Subject Id",
      width: 90,
      align: "left",
      headerAlign: "left",
      sortable: true,
      filterable: true,
      filterOperators: getGridStringOperators().filter((op) => op.value === "contains"),
      valueGetter: (_v, row) => row?.all_data?.participant_id ?? "",
    },

    formatted_intials: {
      field: "formatted_intials",
      headerName: "SiteID-SubjectsID",
      width: 100,
      align: "left",
      headerAlign: "left",
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => row?.all_data?.formatted_intials ?? "",
    },

    completed_photo_session_count: {
      field: "completed_photo_session_count",
      headerName: "Completed Visits",
      type: "number",
      width: 120,
      align: "left",
      headerAlign: "left",
      sortable: true,
      filterable: true,
      filterOperators: getGridNumericOperators().filter((op) =>
        numberFiltersAllowed.includes(op.value)
      ),
      valueGetter: (_v, row) => row?.all_data?.completed_photo_session_count ?? null,
    },

    first_name: {
      field: "first_name",
      headerName: "First Name",
      width: 100,
      align: "left",
      headerAlign: "left",
      sortable: false,
      filterOperators: getGridStringOperators().filter((op) =>
        ["contains", "equals"].includes(op.value)
      ),
      valueGetter: (_v, row) => row?.first_name ?? "",
    },

    last_name: {
      field: "last_name",
      headerName: "Last Name",
      width: 100,
      align: "left",
      headerAlign: "left",
      sortable: false,
      filterOperators: getGridStringOperators().filter((op) =>
        ["contains", "equals"].includes(op.value)
      ),
      valueGetter: (_v, row) => row?.last_name ?? "",
    },

    email: {
      field: "email",
      headerName: "Email (Subject)",
      width: 150,
      align: "left",
      headerAlign: "left",
      sortable: false,
      filterOperators: getGridStringOperators().filter((op) =>
        ["contains", "equals"].includes(op.value)
      ),
      valueGetter: (_v, row) => row?.all_data?.email ?? "",
    },

    clinic: {
      field: "clinic",
      headerName: "Clinic",
      width: 150,
      align: "left",
      headerAlign: "left",
      sortable: false,
      valueGetter: (_v, row) => row?.clinic ?? "",
    },

    has_trial: {
      field: "has_trial",
      headerName: "Has Trial",
      width: 200,
      align: "left",
      headerAlign: "left",
      sortable: false,
      valueGetter: (_v, row) => row?.all_data?.has_trial ?? "",
    },

    upcoming_visit: {
      field: "upcoming_visit",
      headerName: "Upcoming Visit",
      width: 200,
      align: "left",
      headerAlign: "left",
      sortable: false,
      valueGetter: (_v, row) => {
        const v = row?.all_data?.upcoming_or_current_visit_dict;
        if (!v?.visit) return "";
        return `${v.visit}${v.ongoing ? " [Ongoing]" : ""}`;
      },
    },

    upcoming_visit_start_date: {
      field: "upcoming_visit_start_date",
      headerName: "Upcoming Visit Start Date",
      type: "dateTime",
      width: 200,
      align: "left",
      headerAlign: "left",
      sortable: false,
      valueGetter: (_v, row) => {
        const d = row?.all_data?.upcoming_or_current_visit_dict?.start_date;
        return d ? new Date(d + "Z") : null;
      },
    },

    external_photos_count: {
      field: "external_photos_count",
      headerName: "External Photos Count",
      width: 150,
      valueGetter: (_v, row) => row?.all_data?.external_photos_count ?? 0,
    },

    external_videos_count: {
      field: "external_videos_count",
      headerName: "External Videos Count",
      width: 150,
      valueGetter: (_v, row) => row?.all_data?.external_videos_count ?? 0,
    },

    external_docs_count: {
      field: "external_docs_count",
      headerName: "External Documents Count",
      width: 150,
      valueGetter: (_v, row) => row?.all_data?.external_docs_count ?? 0,
    },

    unread_messages: {
      field: "unread_messages",
      headerName: "Unread Messages",
      valueGetter: (_v, row) => row?.all_data?.unread_messages?.length ?? 0,
    },

    vip: {
      field: "vip",
      headerName: "VIP",
      valueGetter: (_v, row) => (row?.vip === false ? "No" : "Yes"),
    },

    fired: {
      field: "fired",
      headerName: "FIRED",
      valueGetter: (_v, row) => (row?.fired === false ? "No" : "Yes"),
    },

    do_not_call: {
      field: "do_not_call",
      headerName: "DO NOT CALL",
      width: 160,
      valueGetter: (_v, row) => (row?.do_not_call === false ? "No" : "Yes"),
    },

    birth_year: {
      field: "birth_year",
      headerName: "Birth Year",
      valueGetter: (_v, row) => row?.birth_date ?? "",
    },

    email_crc: {
      field: "email_crc",
      headerName: "Last InClinic Activity CRC/INV (Email)",
      width: 200,
      valueGetter: (_v, row) => row?.all_data?.email_crc ?? "",
    },

    last_activity_crc: {
      field: "last_activity_crc",
      headerName: "Last InClinic Activity",
      width: 300,
      sortable: true,
      valueGetter: (_v, row) => {
        const d = row?.all_data?.last_activity_crc;
        return d ? new Date(d + "Z").toLocaleString() : "";
      },
    },

    last_inapp_visit_date: {
      field: "last_inapp_visit_date",
      headerName: "Last App Activity",
      width: 220,
      sortable: true,
      valueGetter: (_v, row) => {
        const d = row?.all_data?.last_inapp_visit_date;
        return d ? dayjs.utc(d).local().toDate() : null;
      },
      valueFormatter: (value) =>
        value ? dayjs(value).format("MMM D, YYYY h:mm A") : "no activity",
    },

    age: {
      field: "age",
      headerName: "Age",
      valueGetter: (_v, row) => {
        if (!row?.birth_date) return "";
        const birth = new Date(row.birth_date);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        if (
          today.getMonth() < birth.getMonth() ||
          (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
        ) {
          age--;
        }
        return age;
      },
    },

    preferred_language: {
      field: "preferred_language",
      headerName: "Preferred Language",
      width: 140,
      align: "left",
      headerAlign: "left",
      sortable: true,
      filterable: true,
      type: "singleSelect",
      valueOptions: [
        { value: "en", label: "English" },
        { value: "es", label: "Spanish" },
        { value: "de", label: "German" },
      ],
      valueGetter: (_v, row) => row?.preferred_language ?? "en",
      valueFormatter: (value) => {
        const langMap = { en: "English", es: "Spanish", de: "German" };
        return langMap[value] ?? value;
      },
    },
  };

  const visibleFields = useMemo(() => {
    if (!columnVisibilityModel) return "";
    const result = Object.keys(columnVisibilityModel)
      .filter((key) => columnVisibilityModel[key] === true)
      .join(",");
    return result;
  }, [columnVisibilityModel]);

  const getParams = (nextPage, search, typeOfPatient, visibleFields) => {
    const params = new URLSearchParams({
      page: nextPage,
      page_size: search.page_size,
      first_name: search.first_name,
      first_name_operator: search.first_name_operator,
      last_name: search.last_name,
      last_name_operator: search.last_name_operator,
      completed_photo_session_count: search.completed_photo_session_count,
      clinic: search.clinic,
      email: search.email,
      email_operator: search.email_operator,
      trial_name: search.trial_name,
      trial_name_operator: search.trial_name_operator,
      trial_status: search.trial_status,
      participant_id: search.participant_id,
      site_id: search.site_id,
      ordering: search.ordering,
      has_trial: search.has_trial,
      patient_status: search.patient_status,
      patient_status_operator: search.patient_status_operator,
      preferred_language: search.preferred_language,
      preferred_language_operator: search.preferred_language_operator,
      type_of_patient: typeOfPatient,
      drug_id: search.drug_id,
      upcoming_visit_start_date: search.upcoming_visit_start_date,
      fields: visibleFields,
    });

    return params.toString();
  };

  const handleScrollEnd = useCallback(() => {
    if (loaderContext.loaderCount > 0 || !hasMore) return;

    const nextPage = page + 1;

    loaderContext.setLoaderCount((prev) => prev + 1);
    ApiManager.get(
      `${__API_URL__}/api/patient/patient/allpatient/?${getParams(
        nextPage,
        search,
        typeOfPatient,
        visibleFields
      )}`,
      TOKEN
    )
      .then((res) => {
        if (res.data.results.length === 0) {
          setHasMore(false);
        } else {
          setData((prevData) => ({
            ...prevData,
            results: [...prevData.results, ...res.data.results],
          }));
          setPage(nextPage);
        }
      })
      .catch((error) => {
        if (error.response.data.detail === "Invalid page.") {
          setHasMore(false);
        } else {
          console.error("Error fetching more data:", error);
        }
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  }, [page, hasMore, patientTableConfigurations?.noOfRows, loaderContext.loaderCount]);

  useEffect(() => {
    if (data?.results?.length === data?.count) {
      setHasMore(false);
    }
  }, [data.results]);

  useEffect(() => {
    if (!apiRef.current || !data?.results?.length) return;

    const timeout = setTimeout(() => {
      const virtualScrollerEl =
        apiRef.current.virtualScrollerRef?.current ??
        apiRef.current.rootElementRef?.current?.querySelector(".MuiDataGrid-virtualScroller");

      if (!virtualScrollerEl) {
        console.warn("Virtual scroller element not found.");
        return;
      }

      const viewportHeight = virtualScrollerEl.clientHeight;
      const rowHeight = 52;
      const totalRowsHeight = rowHeight * data.results.length;

      if (totalRowsHeight - 400 < viewportHeight && data?.results?.length !== data?.count) {
        handleScrollEnd();
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [data?.results, handleScrollEnd]);

  const handleSaveFilters = async () => {
    saveColumnProperties();
    await ApiManager.patch(
      `${__API_URL__}/api/patient/patient/update_filters/`,
      { ...patientTableConfigurations, visible_column: columnVisibilityModel },
      TOKEN
    );

    onFilterChange(localFilterModel);
    setPage(1);
    setHasMore(true);
    setFilterConfigurationLastStateSaved(true);
  };

  const handleCSVButtonClick = () => {
    apiRef.current.exportDataAsCsv();

    toast.success("Data exported successfully");
    logUserAction("Click on Export to Excel Button");
  };

  const getSelectedPatients = () => {
    if (apiRef?.current?.getSelectedRows) {
      const selectedPatients = apiRef.current.getSelectedRows();

      return Array.from(selectedPatients.values()).map((patient) => ({
        email: patient?.all_data?.email,
        trial_id: patient?.all_data?.trial_id ?? null,
      }));
    }
    return [];
  };

  const handleConfirmDeletePatients = () => {
    const selectedPatients = getSelectedPatients();

    deletePatientsMutation.mutate(
      { patientsList: selectedPatients },
      {
        onSuccess: () => {
          toast.success("Subjects deleted successfully");
          setRefetchPatients(true);
          setOpenConfirmDeletePatientsDialog(false);
        },
        onError: (error) => {
          const errorMsg =
            error?.response?.data?.error ||
            error?.message ||
            "Something went wrong while deleting subjects";
          toast.error(errorMsg);
        },
      }
    );

    logUserAction("Click on Delete Selected Subject");
  };

  useEffect(() => {
    if (selectedValue === "") {
      return;
    }

    isFromDropdown.current = true;

    const newFilterModel = {
      ...localFilterModel,
      items: [
        // Remove existing trial_name filter
        ...localFilterModel.items.filter((item) => item.field !== "trial_name"),
        ...(selectedValue !== "None"
          ? [
              {
                field: "trial_name",
                operator: "equals",
                value: selectedValue,
              },
            ]
          : []),
      ],
    };

    setLocalFilterModel(newFilterModel);
    onFilterChange(newFilterModel);

    setPage(1);
    setHasMore(true);
  }, [selectedValue]);

  const handleChangeTrial = (event) => {
    const value = event.target.value;
    setSelectedValue(value);
  };

  const CustomFooter = ({ rowCount }) => (
    <div style={{ padding: 8, textAlign: "right" }}>Total Rows: {rowCount}</div>
  );

  const handleRowClick = useCallback(
    (params, event) => {
      logUserAction(
        "Clicked on a subject in the Subjects table.",
        params.row.all_data.email,
        params.row?.all_data?.trial_name
      );

      const trialQuery = `?trial=${params.row.all_data.trial_id || 0}`;

      // If Ctrl (Windows/Linux) or Cmd (Mac) is pressed, open in new tab
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault(); // Prevent default left-click navigation
        let path = "";
        if (role === "Reviewer") {
          path = `/patient/${params.row?.id}/photos${trialQuery}`;
        } else if (
          role === "Investigator" ||
          role === "Inv_coordinator" ||
          role === "Sub_investigator" ||
          role === "CRO Admin" ||
          role === "Cro_coordinator" ||
          role === "Data Manager"
        ) {
          path = `/patient/${params.row?.id}${trialQuery}`;
        } else if (
          role === "Super Admin" ||
          role === "Clinic Owner" ||
          role === "Clinic Admin" ||
          role === "Practitioner"
        ) {
          path = `/patient/${params.row?.id}/info${trialQuery}`;
        }
        if (path) {
          window.open(path, "_blank");
        }
      } else {
        // Normal left-click, navigate on the current page
        if (
          role === "Investigator" ||
          role === "Inv_coordinator" ||
          role === "Sub_investigator" ||
          role === "CRO Admin" ||
          role === "Cro_coordinator" ||
          role === "Data Manager" ||
          role === "Reviewer"
        ) {
          navigate(`/patient/${params.row?.id}${trialQuery}`);
        } else if (
          role === "Super Admin" ||
          role === "Clinic Owner" ||
          role === "Clinic Admin" ||
          role === "Practitioner"
        ) {
          navigate(`/patient/${params.row?.id}/info${trialQuery}`);
        }
      }
    },
    [navigate, role]
  );

  const GetPatientFilters = async () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    ApiManager.get(`${__API_URL__}/api/patient/patient/get_patient_filters/`, TOKEN)
      .then((response) => {
        setPatientTableConfigurations((prevConfig) => ({
          filters: response.data.filters,
          sort: response.data.sort,
          density: response.data.density,
          noOfRows: response.data.rows ? response.data.rows : 50,
        }));
        setColumnVisibilityModel(response.data.visible_column);
        setApiCallStatus(true);
      })
      .catch((error) => {
        console.error("Error fetching patient filters:", error);
        toast.error("Failed to fetch patient filters. Please try again later.");
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  useEffect(() => {
    setLocalFilterModel({ items: patientTableConfigurations.filters });
  }, [patientTableConfigurations.filters]);

  const debouncedFilterChange = useCallback(
    debounce((newFilterModel) => {
      onFilterChange(newFilterModel);
    }, 1250),
    []
  );

  const onFilterChange = (filterModel) => {
    let customObject = {};

    setPatientTableConfigurations((prev) => {
      return { ...prev, filters: filterModel.items };
    });

    filterModel.items.forEach((item) => {
      if (!item.value) item.value = "";

      if (item.field === "trial_name") {
        if (!isFromDropdown.current) {
          setSelectedValue("");
        } else {
          isFromDropdown.current = false;
        }
        customObject["trial_name_operator"] = item.operator;
      } else if (item.field === "email") {
        customObject["email_operator"] = item.operator;
      } else if (item.field === "first_name") {
        customObject["first_name_operator"] = item.operator;
      } else if (item.field === "last_name") {
        customObject["last_name_operator"] = item.operator;
      } else if (item.field === "patient_status") {
        customObject["patient_status_operator"] = item.operator;
      } else if (item.field === "preferred_language") {
        customObject["preferred_language_operator"] = item.operator;
      }

      if (["completed_photo_session_count", "upcoming_visit_start_date"].includes(item.field)) {
        customObject[item.field] = JSON.stringify({
          value: item.value,
          operator: item.operator,
        });
      } else {
        customObject[item.field] = item.value;
      }
    });

    setSearch((search) => ({
      ...defaultSearchObject,
      ...customObject,
      page: 1,
    }));
    setFilterConfigurationLastStateSaved(false);
  };

  useEffect(() => {
    if (!initialRenderForSearch.current) {
      loaderContext.setLoaderCount((prev) => prev + 1);

      ApiManager.get(
        `${__API_URL__}/api/patient/patient/allpatient/?${getParams(
          search.page,
          search,
          typeOfPatient,
          visibleFields
        )}`,
        TOKEN
      )
        .then((response) => {
          setData(response.data);
          setPage(1);
          setRefetchPatients(false);

          if (response.data.results.length === 0) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        })
        .finally(() => {
          loaderContext.setLoaderCount((prev) => prev - 1);
        });
    } else initialRenderForSearch.current = false;
  }, [
    search,
    typeOfPatient,
    clinicPatient,
    changedPatientTable === true,
    refetchPatients === true,
  ]);

  useEffect(() => {
    if (apiCallStatus) {
      let customObject = {};
      patientTableConfigurations.filters.forEach((filter) => {
        if (!filter.value) {
          filter.value = "";
        }
        if (filter.field === "completed_photo_session_count") {
          customObject[filter.field] = JSON.stringify({
            value: filter.value,
            operator: filter.operator,
          });
        } else {
          if (
            filter.field === "email" ||
            filter.field === "first_name" ||
            filter.field === "last_name" ||
            filter.field === "trial_name" ||
            filter.field === "patient_status" ||
            filter.field === "preferred_language"
          ) {
            customObject[`${filter.field}_operator`] = filter.operator;
          }
          customObject[filter.field] = filter.value;
          customObject.value = filter.value;
          customObject.operator = filter.operator;
        }
      });

      patientTableConfigurations.sort.forEach((sortObject) => {
        const ordering = sortObject.sort === "desc" ? `-${sortObject.field}` : sortObject.field;
        customObject["ordering"] = ordering;
      });

      setSearch({
        ...defaultSearchObject,
        ...customObject,
        itemCount: patientTableConfigurations.noOfRows,
      });
    }
  }, [apiCallStatus]);

  const getTrialsList = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/photos/trial_status`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setTrialNames((prev) => [...prev, ...res.data]);
      })
      .catch((err) => {
        if (err === "Error: Request failed with status code 401") {
          user.actions.logout();
        }
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const handleSortModelChange = (sortModel) => {
    setPage(1);
    setHasMore(true);
    setPatientTableConfigurations((prev) => ({ ...prev, sort: sortModel }));

    let ordering;
    if (sortModel && sortModel.length > 0) {
      const field = sortModel[0]?.field;
      const sort = sortModel[0]?.sort;
      if (field && sort) {
        if (sort === "desc") {
          ordering = `-${field}`;
        } else if (sort === "asc") {
          ordering = field;
        } else {
          ordering = "";
        }

        setSearch((search) => ({
          ...search,
          ordering,
          page: 1,
        }));
      }
    } else {
      setSearch((search) => ({
        ...search,
        ordering: "unsort",
        page: 1,
      }));
    }
    setFilterConfigurationLastStateSaved(false);
  };

  const getAllColumns = async () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    ApiManager.get(`${__API_URL__}/api/patient/patient/get_columns/`, TOKEN)
      .then((response) => {
        let columnList = [];
        response.data.forEach((col) => {
          if (myColumnsObject[col.field]) {
            columnList.push({
              ...myColumnsObject[col.field],
              width: col.column_width,
            });
          }
        });
        setColumns(columnList);
      })
      .catch((error) => {
        console.error("Error fetching all columns data:", error);
        toast.error("Failed to fetch all columns data data. Please try again later.");
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const saveColumnProperties = async () => {
    let customColumnList = [];
    let count = 1;
    apiRef.current.getAllColumns().forEach((col) => {
      if (col.field !== "__check__") {
        let customColumnObject = {
          field: col.field,
          order_number: count,
          column_width: col.width,
        };
        customColumnList.push(customColumnObject);
        count += 1;
      }
    });
    await ApiManager.patch(
      `${__API_URL__}/api/patient/patient/update_columns/`,
      customColumnList,
      TOKEN
    );
  };

  useEffect(() => {
    GetPatientFilters();
    getAllColumns();
    getTrialsList();
  }, []);

  useEffect(() => {
    return apiRef.current.subscribeEvent("columnHeaderDragEnd", () =>
      setFilterConfigurationLastStateSaved(false)
    );
  }, [apiRef]);

  return (
    <>
      <Box
        className="data-grid-size "
        sx={{
          height: "calc(100vh - 160px)",
          width: "100%",
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          columnVisibilityModel={columnVisibilityModel}
          density={
            ["comfortable", "compact", "standard"].includes(patientTableConfigurations?.density)
              ? patientTableConfigurations.density
              : "standard"
          }
          onColumnVisibilityModelChange={(newModel) => {
            setColumnVisibilityModel(newModel);
            setFilterConfigurationLastStateSaved(false);
          }}
          showToolbar
          slots={{
            toolbar: CustomToolbar,
            footer: () => <CustomFooter rowCount={data?.count} />,
            columnsPanel: CustomColumnsPanel,
            loadingOverlay: LinearProgress,
          }}
          loading={loaderContext.loaderCount > 0}
          rows={data?.results}
          getRowId={(row) => `${row.id}-${row.all_data?.trial_id ?? "none"}`}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          onColumnResize={() => setFilterConfigurationLastStateSaved(false)}
          keepNonExistentRowsSelected
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newModel) => {
            setRowSelectionModel(newModel?.ids instanceof Set ? newModel : EMPTY_SELECTION);
          }}
          slotProps={{
            toolbar: {
              selectedValue,
              handleChangeTrial,
              trialNames,
              setPatientTableConfigurations,
              patientTableConfigurations,
              setFilterConfigurationLastStateSaved,
              handleSaveFilters,
              filterConfigurationLastStateSaved,
              handleCSVButtonClick,
              role,
              setOpenConfirmDeletePatientsDialog,
              selectedCount: rowSelectionModel?.ids?.size ?? 0,
              handleSortModelChange,
            },
            columnsPanel: {
              disableHideAllButton: true,
              disableShowAllButton: true,
            },
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: {
                  style: { display: "none" },
                },
                columnInputProps: {
                  SelectDisplayProps: { "data-testid": "patient-filter-column-select" },
                },
                operatorInputProps: {
                  SelectDisplayProps: { "data-testid": "patient-filter-operator-select" },
                },
                valueInputProps: {
                  InputComponentProps: { "data-testid": "patient-filter-value-input" },
                },
              },
            },
          }}
          sortModel={patientTableConfigurations.sort}
          filterMode="server"
          onFilterModelChange={(newModel) => {
            setLocalFilterModel(newModel);
            debouncedFilterChange(newModel);
            setPage(1);
            setHasMore(true);
          }}
          filterModel={localFilterModel}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          onRowsScrollEnd={handleScrollEnd}
          getRowClassName={(params) =>
            params.row.all_data?.email
              ? `row-email-${params.row.all_data.email.replace(/[@.]/g, "_")}`
              : "row-email-none"
          }
        />
      </Box>

      <Dialog
        open={openConfirmDeletePatientsDialog}
        onClose={() => setOpenConfirmDeletePatientsDialog(false)}
        aria-labelledby="confirm-delete-dialog"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="confirm-delete-dialog">Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the selected{" "}
            <strong>{getSelectedPatients().length}</strong> row(s)?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            For subjects enrolled in multiple trials, only the selected trial enrollment will be
            removed. Subjects with no remaining trial enrollments will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDeletePatientsDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeletePatients}
            color="error"
            variant="contained"
            data-testid="confirm-delete-patients-btn"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
